import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@material-ui/core';
import { first } from 'lodash/fp';
import { getFullName } from '../../../utils';
import { useDateTimeService } from '../../../utils/hooks/dateTimeService';
import { useUser } from '../../../utils/hooks/auth';
import { usePriceService } from '../../../utils/hooks/priceService';
import { MicroAvatar, Typography } from '../../../ui-kit';
import { Employee } from './types';

interface CashFlowRightColumnProps {
    finishedBy: Employee;
    finishedUtc: Date;
    expectedCashAmount: number;
    finalCashAmount: number;
    expectedCreditCardAmount: number;
    finalCreditCardAmount: number;
    cashIncome: number;
}

export const CashFlowRightColumn = ({
    finishedBy: { account },
    finishedUtc,
    expectedCashAmount,
    finalCashAmount,
    expectedCreditCardAmount,
    finalCreditCardAmount,
    cashIncome,
}: CashFlowRightColumnProps) => {
    const { dateTimeService } = useDateTimeService();
    const { user: { cultureCode } } = useUser();
    const { priceService } = usePriceService();
    const { t } = useTranslation();

    const closedDateTime = dateTimeService.localizeDateTime(finishedUtc, cultureCode);
    const fullName = getFullName(account);
    const initial = first(account.firstName);

    const expectedCash = priceService.toLocaleString(expectedCashAmount);
    const finalCash = priceService.toLocaleString(finalCashAmount);
    const differenceCash = priceService.toLocaleString(finalCashAmount - expectedCashAmount);

    const expectedPaymentCard = priceService.toLocaleString(expectedCreditCardAmount);
    const finalPaymentCard = priceService.toLocaleString(finalCreditCardAmount);
    const differencePaymentCard = priceService.toLocaleString(finalCreditCardAmount - expectedCreditCardAmount);

    const formattedCashIncome = priceService.toLocaleString(cashIncome);

    return (
        <Box p={3}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography uppercase color='textSecondary' variant='caption'>{t('Closed at')}</Typography>
                    <Typography>{closedDateTime}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography uppercase color='textSecondary' variant='caption'>{t('Closed by')}</Typography>
                    <Box display='flex'>
                        <Box mr={1}>
                            <MicroAvatar src={account.imageUrl || undefined}>{initial}</MicroAvatar>
                        </Box>
                        <Typography>{fullName}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography uppercase color='textSecondary' variant='caption'>{t('Cash')}</Typography>
                    <Box display='flex'>
                        <Box width={150}><Typography>{t('Expected:')}</Typography></Box>
                        <Box><Typography>{expectedCash}</Typography></Box>
                    </Box>
                    <Box display='flex'>
                        <Box width={150}><Typography>{t('Real:')}</Typography></Box>
                        <Box><Typography>{finalCash}</Typography></Box>
                    </Box>
                    <Box display='flex'>
                        <Box width={150}><Typography>{t('Difference:')}</Typography></Box>
                        <Box><Typography>{differenceCash}</Typography></Box>
                    </Box>
                    <Box display='flex'>
                        <Box width={150}><Typography>{t('Cash income:')}</Typography></Box>
                        <Box><Typography>{formattedCashIncome}</Typography></Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography uppercase color='textSecondary' variant='caption'>{t('Payment card')}</Typography>
                    <Box display='flex'>
                        <Box width={150}><Typography>{t('Expected:')}</Typography></Box>
                        <Box><Typography>{expectedPaymentCard}</Typography></Box>
                    </Box>
                    <Box display='flex'>
                        <Box width={150}><Typography>{t('Real:')}</Typography></Box>
                        <Box><Typography>{finalPaymentCard}</Typography></Box>
                    </Box>
                    <Box display='flex'>
                        <Box width={150}><Typography>{t('Difference:')}</Typography></Box>
                        <Box><Typography>{differencePaymentCard}</Typography></Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};
